/**
 * Name: NavBar
 * Author: @Gaurav
 * Date: 09-21-2020
 */
import React, { useRef, useState, useEffect } from 'react';
import { Breadcrumbs, Typography, Link } from '@material-ui/core';
import { Slider } from '@material-ui/core';
import { formatTime } from '../../utils/DateUtils';
import { MEDIA_URL } from '../../utils/consts';
import './NavBar.scss';

const NavBar = (props) => {
  const {
    noBreadCrumb,
    currentRouteName = '',
    breadcrumbsPath = '',
    text = '',
    showPlayer = false,
    currentSong = null,
    onNextSong = null,
    hasNextSong = false,
    onPrevSong = null,
    hasPrevSong = false,
    onClosePlayer = null,
    showAddToPlaylist = false,
    isCurrentSongAddedToPlaylist = false,
    onToggleAddToPlaylist = null,
  } = props;

  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSeek = (event, newValue) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  const handleTimeUpdate = (event) => {
    setCurrentTime(event.target.currentTime);
    setDuration(event.target.duration);
  };

  const handleLoadedMetadata = (event) => {
    setCurrentTime(event.target.currentTime);
    setDuration(event.target.duration);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSongEnd = () => {
    setIsPlaying(false);
    if (hasNextSong && onNextSong) {
      onNextSong();
    }
  };

  const handleLoadStart = () => {
    setIsLoading(true);
  };

  const handleCanPlay = () => {
    setIsLoading(false);
  };

  const handleClosePlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsLoading(false);
    if (onClosePlayer) {
      onClosePlayer();
    }
  };

  useEffect(() => {
    if (currentSong && audioRef.current) {
      setIsLoading(true);
      audioRef.current.load();
      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(() => {
          setIsPlaying(false);
        });
    }
  }, [currentSong]);

  return (
    <>
      <div className="navBar">
        <h3>{props.data}</h3>
        {showPlayer && currentSong && (
          <div className="navbar-player">
            <audio
              ref={audioRef}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleSongEnd}
              onLoadStart={handleLoadStart}
              onCanPlay={handleCanPlay}
              crossOrigin="anonymous"
              preload="auto"
            >
              <source src={`${MEDIA_URL}songs/${currentSong.id}.mp3`} type="audio/mpeg" />
            </audio>

            <div className="navbar-player-info">
              <span className="navbar-player-title">{currentSong?.name || currentSong?.fileName || currentSong?.title || 'Unknown'}</span>
              <span className="navbar-player-artist">{currentSong?.artist || 'Unknown Artist'}</span>
            </div>

            <div className="navbar-player-controls">
              {hasPrevSong && (
                <button className="navbar-player-btn navbar-player-prev" onClick={onPrevSong}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M18 18L9.5 12L18 6V18ZM8 18V6H6V18H8Z" fill="#40189D" />
                  </svg>
                </button>
              )}

              <button className="navbar-player-btn" onClick={handlePlayPause} disabled={isLoading}>
                {isLoading ? (
                  <div className="navbar-player-loader">
                    <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="20" cy="20" r="16" stroke="#E0D4F7" strokeWidth="4" fill="none" />
                      <path d="M20 4C28.8366 4 36 11.1634 36 20" stroke="#40189D" strokeWidth="4" strokeLinecap="round" fill="none">
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          from="0 20 20"
                          to="360 20 20"
                          dur="1s"
                          repeatCount="indefinite"
                        />
                      </path>
                    </svg>
                  </div>
                ) : isPlaying ? (
                  <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M36 20C36 28.8366 28.8366 36 20 36C11.1634 36 4 28.8366 4 20C4 11.1634 11.1634 4 20 4C28.8366 4 36 11.1634 36 20Z" fill="#6B3EB4" />
                    <path d="M16 16C16 15.4477 16.4477 15 17 15H18C18.5523 15 19 15.4477 19 16V24C19 24.5523 18.5523 25 18 25H17C16.4477 25 16 24.5523 16 24V16Z" fill="white" />
                    <path d="M22 16C22 15.4477 22.4477 15 23 15H24C24.5523 15 25 15.4477 25 16V24C25 24.5523 24.5523 25 24 25H23C22.4477 25 22 24.5523 22 24V16Z" fill="white" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 40 40" fill="none">
                    <path d="M20.1761 3.3335C16.845 3.3335 13.5888 4.32127 10.8191 6.1719C8.04945 8.02254 5.89076 10.6529 4.61602 13.7304C3.34128 16.8079 3.00775 20.1943 3.65761 23.4613C4.30746 26.7284 5.91152 29.7294 8.26693 32.0848C10.6223 34.4402 13.6233 36.0442 16.8904 36.6941C20.1574 37.3439 23.5438 37.0104 26.6213 35.7357C29.6988 34.4609 32.3292 32.3022 34.1798 29.5326C36.0304 26.7629 37.0182 23.5067 37.0182 20.1756C37.0182 17.9639 36.5826 15.7738 35.7362 13.7304C34.8898 11.687 33.6492 9.83037 32.0853 8.26643C30.5213 6.7025 28.6647 5.46192 26.6213 4.61553C24.5779 3.76913 22.3878 3.3335 20.1761 3.3335ZM16.8904 25.7236V13.7407L26.1204 20.1756L16.8904 25.7236Z" fill="#40189D" />
                  </svg>
                )}
              </button>

              {hasNextSong && (
                <button className="navbar-player-btn navbar-player-next" onClick={onNextSong}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M6 18L14.5 12L6 6V18ZM16 6V18H18V6H16Z" fill="#40189D" />
                  </svg>
                </button>
              )}

              {showAddToPlaylist && typeof onToggleAddToPlaylist === 'function' && (
                <button
                  className={`navbar-player-btn navbar-player-add ${isCurrentSongAddedToPlaylist ? 'is-added' : ''}`}
                  onClick={() => onToggleAddToPlaylist(currentSong)}
                  title={isCurrentSongAddedToPlaylist ? 'Remove from selected songs' : 'Add to selected songs'}
                  aria-label={isCurrentSongAddedToPlaylist ? 'Remove from selected songs' : 'Add to selected songs'}
                >
                  {isCurrentSongAddedToPlaylist ? (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2Z"
                        fill="#6B3EB4"
                      />
                      <path
                        d="M10.2 15.4L6.8 12L5.7 13.1L10.2 17.6L18.3 9.5L17.2 8.4L10.2 15.4Z"
                        fill="white"
                      />
                    </svg>
                  ) : (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22Z"
                        fill="#F0E8FF"
                        stroke="#40189D"
                        strokeWidth="1"
                      />
                      <path d="M12 7V17" stroke="#40189D" strokeWidth="2" strokeLinecap="round" />
                      <path d="M7 12H17" stroke="#40189D" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  )}
                </button>
              )}
            </div>

            <div className="navbar-player-progress">
              <Slider
                size="small"
                value={currentTime}
                onChange={handleSeek}
                max={duration || 100}
                aria-label="Song progress"
                style={{ width: 120 }}
              />
              <span className="navbar-player-time">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <button className="navbar-player-btn navbar-player-close" onClick={handleClosePlayer} title="Close player">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="#40189D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        )}
      </div>
      {!noBreadCrumb ? (
        <>
          <div className="wrap_breadcum">
            <Breadcrumbs aria-label="breadcrumb">
              <Link className="active_link" to="/">
                {currentRouteName}
              </Link>
              <Typography className="bredcum_link">{breadcrumbsPath}
                {text &&
                  <span className="bredcum_link">  ({text})</span>
                }
              </Typography>
            </Breadcrumbs>
          </div>
        </>
      ) : null}
    </>
  );
};
export default NavBar;
